import React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import TableHead from "@mui/material/TableHead";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import { nameToTooltipLonger } from "./DashbordUtils/nameToTooltipLonger";
import { OrangeTextButton } from "../../common/OrangeTextButton";
import BackdropLoadingScreen from "../../common/BackdropLoadingScreen";
import { useNavigate } from "react-router-dom";
import { useDashboardStore } from "./store/dashboardStore";

const formatDateFromEpoch = (epoch: number): string => {
  const date = new Date(epoch * 1000); // Convert epoch seconds to milliseconds

  const options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "2-digit",
    year: "numeric",
  };

  return date.toLocaleDateString("en-US", options);
};

export const ErrorTab = () => {
  const navigate = useNavigate();

  // Get all state and actions directly from the store
  const {
    activeTab,
    camerasErrorInfo,
    camerasWarningInfo,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    setPageLoading,
    loadingMoreData,
    displayShowMoreButton,
    getAdditionalData,
    updateShowMoreButtonState,
    loadingErrorInfo,
    loadingWarningInfo,
  } = useDashboardStore();

  // Determine data source based on activeTab
  const dataToDisplay =
    activeTab === "error" ? camerasErrorInfo : camerasWarningInfo;
  // const isLoading = dataToDisplay.length === 0; // Simple loading check

  // Update the "Load More" button visibility when relevant state changes
  React.useEffect(() => {
    updateShowMoreButtonState();
  }, [
    rowsPerPage,
    page,
    activeTab,
    camerasErrorInfo,
    camerasWarningInfo,
    updateShowMoreButtonState,
  ]);

  // Reset page when tab changes
  React.useEffect(() => {
    setPage(0);
  }, [activeTab, setPage]);

  return (
    <Paper sx={{ overflow: "hidden" }}>
      <BackdropLoadingScreen openState={loadingMoreData} />
      <TableContainer sx={{ maxHeight: 640, width: "100%" }}>
        <Table aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell
                key="site"
                align="left"
                sx={{
                  fontFamily: "Mulish",
                  fontWeight: "Bold",
                  width: "137.5px",
                }}
              >
                Site
              </TableCell>
              <TableCell
                key="camera"
                align="left"
                sx={{
                  fontFamily: "Mulish",
                  fontWeight: "Bold",
                  width: "137.5px",
                }}
              >
                Camera
              </TableCell>
              <TableCell
                key="date"
                align="left"
                sx={{
                  fontFamily: "Mulish",
                  fontWeight: "Bold",
                  width: "137.5px",
                }}
              >
                Date
              </TableCell>
              <TableCell
                key="error"
                align="left"
                sx={{
                  fontFamily: "Mulish",
                  fontWeight: "Bold",
                  width: "137.5px",
                }}
              >
                Error
              </TableCell>
            </TableRow>
          </TableHead>
          {((activeTab === "error" && !loadingErrorInfo) ||
            (activeTab === "warning" && !loadingWarningInfo)) && (
            <TableBody>
              {dataToDisplay
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((object: any) => {
                  return (
                    <TableRow
                      hover
                      key={object.id}
                      role="checkbox"
                      sx={{
                        cursor: "pointer",
                        fontFamily: "Mulish",
                      }}
                      onClick={() => {
                        navigate(
                          `/sites/${object.customer.id}/editcamera/${object.id}`
                        );
                      }}
                    >
                      {nameToTooltipLonger(
                        object.customer.name,
                        "name",
                        setPageLoading,
                        object,
                        navigate
                      )}

                      {nameToTooltipLonger(
                        object.camera_name,
                        "name",
                        setPageLoading,
                        object,
                        navigate
                      )}

                      <TableCell
                        onClick={() => {
                          navigate(
                            `/sites/${object.customer.id}/editcamera/${object.id}`
                          );
                        }}
                        align="left"
                        sx={{ fontFamily: "Mulish", width: "137.5px" }}
                      >
                        {object.date_added
                          ? formatDateFromEpoch(object.date_added)
                          : "--"}
                      </TableCell>
                      {nameToTooltipLonger(
                        object.message,
                        "error",
                        setPageLoading,
                        object,
                        navigate
                      )}
                    </TableRow>
                  );
                })}
              {displayShowMoreButton && (
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell>
                    <OrangeTextButton
                      onClick={() => {
                        getAdditionalData(navigate);
                      }}
                      text="Load More"
                      disabled={false}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          )}
          {((activeTab === "error" && loadingErrorInfo) ||
            (activeTab === "warning" && loadingWarningInfo)) && (
            <TableBody>
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ padding: "20px" }}>
                  <div className="flex flex-col items-center justify-center">
                    <CircularProgress size={25} sx={{ marginBottom: "8px" }} />
                    <div className="font-mulish text-actuate-blue font-bold">
                      Fetching Data...
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>
        {dataToDisplay && (
          <TablePagination
            component="div"
            rowsPerPageOptions={[10, 25, 100]}
            count={dataToDisplay.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event: unknown, newPage: number) => {
              setPage(newPage);
            }}
            onRowsPerPageChange={(event: any) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        )}
      </TableContainer>
    </Paper>
  );
};
